import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Users, Shield, UserCog, Plus, Search, Mail, Phone, Trash2, Calendar, GitMerge, Edit2, Eye, EyeOff, Send, Copy, RefreshCw, X, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { admin as adminApi, employeeInvitations } from '../utils/api';
import { format } from 'date-fns';
import MergeUsersModal from '../components/MergeUsersModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('staff'); // staff (admin+groomer), admin, groomer, client
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showCreateEmployee, setShowCreateEmployee] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showMergeUsers, setShowMergeUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Roadmap item #17 Phase 1: invitations are tracked separately from
  // users until accepted, so they need their own state + loader.
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    loadUsers();
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const res = await employeeInvitations.list();
      setInvitations(res.data || []);
    } catch (err) {
      // Non-fatal — if the endpoint is missing (older install) the panel
      // just stays empty rather than blocking the whole page.
      setInvitations([]);
    }
  };

  const filterUsers = useCallback(() => {
    let filtered = users;

    // Filter by role
    if (roleFilter === 'staff') {
      // Show only admins and employees (groomers)
      filtered = filtered.filter(user => user.role === 'admin' || user.role === 'groomer');
    } else if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.first_name?.toLowerCase().includes(term) ||
        user.last_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, users]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const loadUsers = async () => {
    try {
      const response = await adminApi.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Load users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminApi.updateUserRole(userId, newRole);
      toast.success(`User role updated to ${newRole}`);
      loadUsers();
    } catch (error) {
      console.error('Update role error:', error);
      toast.error(error.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminApi.deleteUser(userId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'groomer':
        return 'bg-blue-100 text-blue-800';
      case 'client':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <UserCog size={32} className="text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold">User Management</h1>
            <p className="text-gray-600">Manage user roles and permissions</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowMergeUsers(true)}
            className="btn bg-purple-600 text-white hover:bg-purple-700 flex items-center space-x-2"
          >
            <GitMerge size={20} />
            <span>Merge Users</span>
          </button>
          {/* Headline action (item #17): the new hire sets their own
              password via the invite link instead of the admin picking
              one for them. Both employee + admin invites flow through
              this single modal. */}
          <button
            onClick={() => setShowInvite(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Send size={18} />
            <span>Invite Employee</span>
          </button>
          {/* Demoted but kept: lets admins pick a password directly when
              the new hire is on-site (no email needed). */}
          <details className="relative">
            <summary className="btn btn-secondary flex items-center space-x-2 cursor-pointer list-none">
              <Plus size={18} />
              <span>Create directly</span>
            </summary>
            <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <button
                onClick={() => setShowCreateEmployee(true)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                Create employee account
              </button>
              <button
                onClick={() => setShowCreateAdmin(true)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-t border-gray-100"
              >
                Create admin account
              </button>
            </div>
          </details>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-red-500 to-red-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Admins</p>
              <p className="text-3xl font-bold mt-1">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
            <Shield size={40} className="text-red-200" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Employees</p>
              <p className="text-3xl font-bold mt-1">
                {users.filter(u => u.role === 'groomer').length}
              </p>
            </div>
            <UserCog size={40} className="text-blue-200" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Staff</p>
              <p className="text-3xl font-bold mt-1">
                {users.filter(u => u.role === 'admin' || u.role === 'groomer').length}
              </p>
            </div>
            <Users size={40} className="text-purple-200" />
          </div>
        </div>
      </div>

      {/* Pending invitations panel (item #17 Phase 1). Hidden when there
          are no rows so the page stays clean for fresh installs. */}
      {invitations.length > 0 && (
        <InvitationsPanel
          invitations={invitations}
          onChange={loadInvitations}
        />
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex space-x-2">
            {['staff', 'admin', 'groomer'].map(role => (
              <button
                key={role}
                onClick={() => setRoleFilter(role)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  roleFilter === role
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {role === 'staff' ? 'All Staff' : role === 'groomer' ? 'Employee' : role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="card text-center py-12">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <div key={user.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold">
                      {user.first_name} {user.last_name}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                      {user.role === 'groomer' ? 'EMPLOYEE' : user.role.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center md:space-x-6 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Mail size={16} />
                      <span>{user.email}</span>
                    </div>
                    {user.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone size={16} />
                        <span>{user.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>Joined {format(new Date(user.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="client">Client</option>
                    <option value="groomer">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                  
                  <button
                    onClick={() => setEditingUser(user)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="View/Edit user"
                  >
                    <Edit2 size={20} />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete user"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Merge Users Modal */}
      {showMergeUsers && (
        <MergeUsersModal
          onClose={() => setShowMergeUsers(false)}
          onSuccess={() => {
            setShowMergeUsers(false);
            loadUsers();
          }}
          allUsers={users}
        />
      )}

      {/* Invite Employee Modal (item #17 Phase 1) */}
      {showInvite && (
        <InviteEmployeeModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            setShowInvite(false);
            loadInvitations();
          }}
        />
      )}

      {/* Create Employee Modal */}
      {showCreateEmployee && (
        <CreateEmployeeModal
          onClose={() => setShowCreateEmployee(false)}
          onSuccess={() => {
            setShowCreateEmployee(false);
            loadUsers();
          }}
        />
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <CreateAdminModal
          onClose={() => setShowCreateAdmin(false)}
          onSuccess={() => {
            setShowCreateAdmin(false);
            loadUsers();
          }}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

const CreateEmployeeModal = ({ onClose, onSuccess }) => {
  const [showEmpPassword, setShowEmpPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    notes: '',
    adminNotes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await adminApi.createGroomer({
        ...formData,
      });
      toast.success('Employee account created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Create employee error:', error);
      toast.error(error.response?.data?.error || 'Failed to create employee account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Create Employee Account</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">Password * <span className="text-gray-400 font-normal text-xs">(min. 6 characters)</span></label>
            <div className="relative">
              <input
                type={showEmpPassword ? 'text' : 'password'}
                required
                minLength={6}
                placeholder="Min. 6 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input pr-10"
              />
              <button
                type="button"
                onMouseDown={() => setShowEmpPassword(true)}
                onMouseUp={() => setShowEmpPassword(false)}
                onMouseLeave={() => setShowEmpPassword(false)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none"
                tabIndex={-1}
                title="Hold to reveal password"
              >
                {showEmpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">The employee will use this to log in. Share it with them directly.</p>
          </div>

          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </div>

          {/* Client-Visible Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs mr-2">CLIENT VISIBLE</span>
              Shown to clients when booking
            </h3>
            
            <div>
              <label className="label">Bio / Description</label>
              <textarea
                rows="3"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="input"
                placeholder="Skills, experience, and description shown to clients..."
              />
              <p className="text-xs text-gray-500 mt-1">This is displayed to clients when selecting an employee during booking.</p>
            </div>
          </div>

          {/* Internal Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs mr-2">INTERNAL</span>
              Visible to employee & admin only
            </h3>
            
            <div>
              <label className="label">Notes</label>
              <textarea
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input"
                placeholder="Internal notes about this employee..."
              />
              <p className="text-xs text-gray-500 mt-1">Only visible to this employee and admins.</p>
            </div>
          </div>

          {/* Admin Only Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center">
              <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs mr-2">ADMIN ONLY</span>
              Private admin notes
            </h3>
            
            <div>
              <label className="label">Admin Notes</label>
              <textarea
                rows="2"
                value={formData.adminNotes}
                onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                className="input"
                placeholder="Private notes only admins can see..."
              />
              <p className="text-xs text-gray-500 mt-1">Only visible to admins. Employee cannot see this.</p>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateAdminModal = ({ onClose, onSuccess }) => {
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await adminApi.createAdmin(formData);
      toast.success('Admin account created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Create admin error:', error);
      toast.error(error.response?.data?.error || 'Failed to create admin account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-4">Create Admin Account</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">Password * <span className="text-gray-400 font-normal text-xs">(min. 6 characters)</span></label>
            <div className="relative">
              <input
                type={showAdminPassword ? 'text' : 'password'}
                required
                minLength={6}
                placeholder="Min. 6 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input pr-10"
              />
              <button
                type="button"
                onMouseDown={() => setShowAdminPassword(true)}
                onMouseUp={() => setShowAdminPassword(false)}
                onMouseLeave={() => setShowAdminPassword(false)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none"
                tabIndex={-1}
                title="Hold to reveal password"
              >
                {showAdminPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">The admin will use this to log in. Share it with them directly.</p>
          </div>

          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({ user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    notes: '',
    adminNotes: '',
    licenseNumber: '',
    professionalAssociation: '',
  });

  useEffect(() => {
    loadUserDetails();
  }, [user.id]);

  const loadUserDetails = async () => {
    try {
      const response = await adminApi.getUserDetails(user.id);
      const data = response.data;
      setFormData({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        bio: data.bio || '',
        notes: data.notes || '',
        adminNotes: data.admin_notes || '',
        licenseNumber: data.license_number || '',
        professionalAssociation: data.professional_association || '',
      });
    } catch (error) {
      console.error('Load user details error:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await adminApi.updateUser(user.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        bio: formData.bio,
        notes: formData.notes,
        adminNotes: formData.adminNotes,
        licenseNumber: formData.licenseNumber,
        professionalAssociation: formData.professionalAssociation,
      });
      toast.success('User updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Update user error:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const isGroomerOrAdmin = user.role === 'groomer' || user.role === 'admin';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">
            Edit {user.role === 'groomer' ? 'Employee' : user.role === 'admin' ? 'Admin' : 'Client'}
          </h2>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            user.role === 'admin' ? 'bg-red-100 text-red-800' :
            user.role === 'groomer' ? 'bg-blue-100 text-blue-800' :
            'bg-green-100 text-green-800'
          }`}>
            {user.role === 'groomer' ? 'EMPLOYEE' : user.role.toUpperCase()}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input"
              />
            </div>

            <div>
              <label className="label">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input"
              />
            </div>

            {/* Employee/Admin specific fields */}
            {isGroomerOrAdmin && (
              <>
                {/* Client-Visible Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs mr-2">CLIENT VISIBLE</span>
                    Shown to clients when booking
                  </h3>
                  
                  <div>
                    <label className="label">Bio / Description</label>
                    <textarea
                      rows="3"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="input"
                      placeholder="Skills, experience, and description shown to clients..."
                    />
                    <p className="text-xs text-gray-500 mt-1">This is displayed to clients when selecting an employee during booking.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="label">License / registration #</label>
                      <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                        className="input"
                        placeholder="e.g. RMT12345"
                        maxLength={80}
                      />
                      <p className="text-xs text-gray-500 mt-1">Printed on insurance-ready invoices when the template enables provider info.</p>
                    </div>
                    <div>
                      <label className="label">Professional association / college</label>
                      <input
                        type="text"
                        value={formData.professionalAssociation}
                        onChange={(e) => setFormData({ ...formData, professionalAssociation: e.target.value })}
                        className="input"
                        placeholder="e.g. CMTO, CCO, RCDSO"
                        maxLength={120}
                      />
                      <p className="text-xs text-gray-500 mt-1">Regulatory body the provider is registered with.</p>
                    </div>
                  </div>
                </div>

                {/* Internal Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs mr-2">INTERNAL</span>
                    Visible to employee & admin only
                  </h3>
                  
                  <div>
                    <label className="label">Notes</label>
                    <textarea
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="input"
                      placeholder="Internal notes about this employee..."
                    />
                  </div>
                </div>

                {/* Admin Only Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center">
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs mr-2">ADMIN ONLY</span>
                    Private admin notes
                  </h3>
                  
                  <div>
                    <label className="label">Admin Notes</label>
                    <textarea
                      rows="2"
                      value={formData.adminNotes}
                      onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                      className="input"
                      placeholder="Private notes only admins can see..."
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn btn-secondary"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Roadmap item #17 Phase 1: invite-by-email components.
//
// InviteEmployeeModal — admin types an email + picks a role; backend
// generates a token, persists the invite, and emails the link. We
// surface the accept_url in the success state so the admin can copy-paste
// it into Slack/SMS/etc. if email delivery fails for any reason.
//
// InvitationsPanel — collapsible list of pending/accepted/expired/revoked
// invitations with resend + revoke + copy-link controls. Hidden when
// there are no invitations on file.
// ──────────────────────────────────────────────────────────────────────

const InviteEmployeeModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ email: '', role: 'groomer' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { accept_url, email_sent } once sent

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await employeeInvitations.create({
        email: form.email.trim(),
        role: form.role,
      });
      setResult(res.data);
      if (res.data?.email_sent) {
        toast.success('Invitation sent');
      } else {
        toast.warning("Invitation created but email didn't send — copy the link below to share manually");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!result?.accept_url) return;
    navigator.clipboard?.writeText(result.accept_url).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy — select and copy manually')
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Invite a team member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {result ? (
          <div className="space-y-4">
            <div className="card border-green-200 bg-green-50">
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-600 shrink-0 mt-0.5" />
                <div className="text-sm text-green-900">
                  <p className="font-medium">Invitation created for {result.email}.</p>
                  <p className="mt-1">
                    {result.email_sent
                      ? 'They should receive an email shortly with a link to accept.'
                      : "Email didn't send — copy the link below and share it directly."}
                  </p>
                  <p className="mt-1 text-xs">Expires {format(new Date(result.expires_at), 'MMM d, yyyy')}</p>
                </div>
              </div>
            </div>
            <div>
              <label className="label">Accept link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={result.accept_url}
                  readOnly
                  className="input flex-1 text-xs bg-gray-50"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="btn btn-secondary flex items-center gap-1"
                >
                  <Copy size={16} /> Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Anyone with this link can claim the {result.role === 'admin' ? 'admin' : 'employee'} role for this email — share carefully.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setResult(null)}
                className="btn btn-secondary"
              >
                Send another
              </button>
              <button
                type="button"
                onClick={onSuccess}
                className="btn btn-primary"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input"
                required
                autoFocus
                placeholder="newhire@example.com"
              />
            </div>
            <div>
              <label className="label">Role</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, role: 'groomer' })}
                  className={`p-3 rounded-lg border text-left transition ${form.role === 'groomer' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <UserCog size={16} /> Employee
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Day-to-day staff (groomer / service provider).</p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, role: 'admin' })}
                  className={`p-3 rounded-lg border text-left transition ${form.role === 'admin' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <Shield size={16} /> Admin
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Full access to settings + every record.</p>
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
              <p className="font-medium">How this works</p>
              <ul className="text-xs mt-1 space-y-0.5 list-disc pl-4">
                <li>They get an email with a one-time link.</li>
                <li>They pick their own password — you never see it.</li>
                <li>The link expires in 7 days; resendable from this page.</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn btn-secondary" disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const InvitationsPanel = ({ invitations, onChange }) => {
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Sort: pending first, then expired, then accepted, then revoked.
  const sorted = [...invitations].sort((a, b) => {
    const order = { pending: 0, expired: 1, accepted: 2, revoked: 3 };
    return (order[a.status] ?? 99) - (order[b.status] ?? 99);
  });

  const pendingCount = invitations.filter((i) => i.status === 'pending').length;

  const handleResend = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await employeeInvitations.resend(inv.id);
      toast.success(res.data?.email_sent ? 'Invitation resent' : 'Token rotated — copy the new link');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Resend failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (inv) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}? The link will stop working immediately.`)) return;
    setBusyId(inv.id);
    try {
      await employeeInvitations.revoke(inv.id);
      toast.success('Invitation revoked');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = (inv) => {
    if (!inv.accept_url) return;
    navigator.clipboard?.writeText(inv.accept_url).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy')
    );
  };

  const statusBadge = (status) => {
    const map = {
      pending:  { className: 'bg-amber-100 text-amber-800',  Icon: Clock,       label: 'Pending'  },
      accepted: { className: 'bg-green-100 text-green-800',  Icon: CheckCircle, label: 'Accepted' },
      expired:  { className: 'bg-gray-100 text-gray-700',    Icon: AlertCircle, label: 'Expired'  },
      revoked:  { className: 'bg-red-100 text-red-700',      Icon: XCircle,     label: 'Revoked'  },
    };
    const s = map[status] || map.pending;
    const Icon = s.Icon;
    return (
      <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${s.className}`}>
        <Icon size={12} /> {s.label}
      </span>
    );
  };

  return (
    <div className="card mb-6">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Send size={18} className="text-primary-600" />
          Pending invitations
          {pendingCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </h3>
        <span className="text-xs text-gray-500">
          {collapsed ? 'Show' : 'Hide'} ({invitations.length})
        </span>
      </button>

      {!collapsed && (
        <ul className="divide-y divide-gray-100 mt-3">
          {sorted.map((inv) => (
            <li key={inv.id} className="py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{inv.email}</span>
                  {statusBadge(inv.status)}
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                    {inv.role === 'admin' ? 'Admin' : 'Employee'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Sent {format(new Date(inv.created_at), 'MMM d, yyyy')}
                  {inv.invited_by_name ? ` by ${inv.invited_by_name}` : ''}
                  {inv.status === 'pending' && ` · expires ${format(new Date(inv.expires_at), 'MMM d')}`}
                  {inv.status === 'accepted' && inv.accepted_at && ` · accepted ${format(new Date(inv.accepted_at), 'MMM d')}`}
                </div>
              </div>
              {inv.status === 'pending' && (
                <div className="flex items-center gap-1">
                  {inv.accept_url && (
                    <button
                      onClick={() => handleCopy(inv)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                      title="Copy accept link"
                      disabled={busyId === inv.id}
                    >
                      <Copy size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleResend(inv)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded"
                    title="Resend (rotates the token)"
                    disabled={busyId === inv.id}
                  >
                    <RefreshCw size={16} />
                  </button>
                  <button
                    onClick={() => handleRevoke(inv)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Revoke"
                    disabled={busyId === inv.id}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default UserManagement;
